import React from 'react';
import ReactDOM from 'react-dom/client';
import { ChatWidget, type ChatWidgetController } from './ChatWidget';
import type { ChatWidgetConfig } from './types';
import { mergeConfig, removeStyles } from './utils';
// @ts-ignore
import styles from './styles.css?inline';

class VinfotechChatWidget {
  private root: ReactDOM.Root | null = null;
  private host: HTMLDivElement | null = null;
  private shadowRoot: ShadowRoot | null = null;
  private container: HTMLDivElement | null = null;
  private config: ChatWidgetConfig | null = null;
  private isInitialized: boolean = false;
  private controller: ChatWidgetController | null = null;

  constructor() {
    if (typeof window !== 'undefined') {
      (window as any).VinfotechChatWidget = this;
    }
  }

  init(userConfig: Partial<ChatWidgetConfig>): void {
    if (this.isInitialized) {
      console.warn('VinfotechChatWidget is already initialized');
      return;
    }

    // Merge config to apply defaults (including API URL)
    this.config = mergeConfig(userConfig);

    // Check validity after merge to ensure we have requirements
    if (!this.config.apiUrl) {
      console.error('Invalid configuration: apiUrl is required');
    }

    this.createContainer();
    this.render();
    this.isInitialized = true;

    const version = this.config.version || '1.0.0';
    console.log(`VinfotechChatWidget v${version} initialized successfully`);
  }

  private createContainer(): void {
    // 1. Create the Host Element
    this.host = document.createElement('div');
    this.host.id = 'vinfotech-chat-widget-host';
    this.host.style.position = 'fixed'; // Ensure it stays in viewport even if page scrolls
    this.host.style.top = '0';
    this.host.style.left = '0';
    this.host.style.width = '0'; // Minimize footprint
    this.host.style.height = '0';
    this.host.style.pointerEvents = 'none'; // Allow clicks to pass through to the page
    this.host.style.zIndex = '2147483647'; // Max z-index

    // 2. Attach Shadow DOM
    this.shadowRoot = this.host.attachShadow({ mode: 'open' });

    // 3. Inject Styles
    const styleElement = document.createElement('style');
    styleElement.textContent = styles;
    this.shadowRoot.appendChild(styleElement);

    // Inject custom user CSS if any
    if (this.config?.customization?.css) {
      const customStyle = document.createElement('style');
      customStyle.textContent = this.config.customization.css;
      this.shadowRoot.appendChild(customStyle);
    }

    // 4. Create the Internal Container (matches Tailwind 'important' selector)
    this.container = document.createElement('div');
    this.container.id = 'vinfotech-chat-widget-root';

    if (this.config?.theme?.primaryColor) {
      this.container.style.setProperty('--vinfo-primary-color', this.config.theme.primaryColor);
    }

    if (this.config?.customization?.className) {
      this.container.className = this.config.customization.className;
    }

    this.shadowRoot.appendChild(this.container);
    document.body.appendChild(this.host);
  }

  private render(): void {
    if (!this.container || !this.config) {
      console.error('Cannot render: container or config is missing');
      return;
    }

    this.root = ReactDOM.createRoot(this.container);
    this.root.render(
      <React.StrictMode>
        {/* Pass shadowRoot to ChatWidget if it needs to query selectors or attach events locally, 
            though mostly it uses React refs which work fine. 
            Window events (resize) work. Portal might be tricky but no portals used. */}
        <ChatWidget
          config={this.config}
          onRegisterController={(c) => {
            this.controller = c;
          }}
        />
      </React.StrictMode>
    );
  }

  destroy(): void {
    this.controller = null;
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }

    if (this.host && this.host.parentNode) {
      this.host.parentNode.removeChild(this.host);
      this.host = null;
    }

    this.shadowRoot = null;
    this.container = null;

    // Remove legacy global styles if any
    removeStyles();
    this.isInitialized = false;
    this.config = null;

    console.log('VinfotechChatWidget destroyed');
  }

  open(): void {
    if (this.controller) {
      this.controller.open();
    } else if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('vinfotech-widget:open'));
    }
  }

  close(): void {
    if (this.controller) {
      this.controller.close();
    } else if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('vinfotech-widget:close'));
    }
  }

  toggle(): void {
    if (this.controller) {
      this.controller.toggle();
    } else if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('vinfotech-widget:toggle'));
    }
  }

  isOpen(): boolean {
    return this.controller ? this.controller.isOpen() : false;
  }

  isReady(): boolean {
    return this.isInitialized;
  }

  getVersion(): string {
    return this.config?.version || '1.0.0';
  }
}

const widget = new VinfotechChatWidget();

if (typeof window !== 'undefined') {
  (window as any).VinfotechChatWidget = widget;

  if ((window as any).vinfotechChatConfig) {
    widget.init((window as any).vinfotechChatConfig);
  }
}

export default VinfotechChatWidget;
export { ChatWidget, type ChatWidgetConfig };
